import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { apiClient } from '../../utils/apiClient';
import { handleAuthStateChange } from '../../store/slices/userSlice';
import Toast from '../../components/shared/Toast';
import SignupPage from './SignupPage';

const SOCIAL_PROVIDERS = [
  {
    id: 'google',
    name: 'Google',
    icon: '/icons/google-icon.svg',
    className: 'bg-white hover:bg-gray-50 text-gray-700'
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    icon: '/icons/microsoft-icon.svg',
    className: 'bg-[#2F2F2F] hover:bg-black text-white'
  },
  {
    id: 'apple',
    name: 'Apple',
    icon: '/icons/apple-icon.svg',
    className: 'bg-white hover:bg-gray-50 text-gray-700'
  }
];

const SigninPage = ({ onClose, onForgotOpen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email.trim()) {
      setError('Email is required.');
      Toast.error('Please enter your email');
      return;
    }

    if (!validateEmail(email)) {
      setIsValidEmail(false);
      setError('Please enter a valid email address.');
      Toast.error('Invalid email format');
      return;
    }

    if (!password) {
      setError('Password is required.');
      Toast.error('Please enter your password');
      return;
    }

    setIsValidEmail(true);
    setIsLoading(true);

    try {
      // Get the affiliate code from localStorage (if it exists)
      const affiliate_code = localStorage.getItem('affiliate_ref');

      // If an affiliate code exists, log it for debugging
      if (affiliate_code) {
        console.log('Found affiliate code during login:', affiliate_code);
      }

      // Proceed with normal login
      const response = await apiClient.auth.login({
        email,
        password,
        // Include affiliate code in login request if available
        ...(affiliate_code && { affiliate_code })
      });

      if (response.status === 'success' && response.data?.token) {
        // After successful login, update the user's affiliate ID if there's a new code
        if (affiliate_code && response.data.user?.id) {
          try {
            await apiClient.user.updateAffiliate({ affiliate_code }, response.data.token);
          } catch (affiliateError) {
            console.error('Failed to update affiliate:', affiliateError);
          }
        }

        dispatch(handleAuthStateChange(response.data.token));
        Toast.success('Successfully logged in!');
        navigate('/dashboard');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Login Error:', err);
      setError('Invalid email or password');
      Toast.error('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const handleSocialLogin = (provider) => {
    try {
      // Get affiliate code from localStorage
      const affiliate_code = localStorage.getItem('affiliate_ref');

      const baseUrl = process.env.REACT_APP_API_URL;

      // If we have an affiliate code, store it in a cookie for the OAuth flow
      if (affiliate_code) {
        // Store the affiliate code in a cookie that will be sent with the request
        document.cookie = `affiliate_ref=${affiliate_code}; path=/; max-age=86400; SameSite=Lax`; // 24 hours
        console.log('Stored affiliate code in cookie for OAuth flow:', affiliate_code);

        // Redirect to the OAuth provider with affiliate code as query parameter
        window.location.href = `${baseUrl}/auth/${provider}?affiliate_code=${encodeURIComponent(affiliate_code)}`;
      } else {
        // Regular OAuth flow without affiliate code
        window.location.href = `${baseUrl}/auth/${provider}`;
      }
    } catch (error) {
      Toast.error(`Failed to connect with ${provider}`);
    }
  };

  return (
    <div className="w-full max-w-md p-6">
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">Sign In to Your Account</h2>
      {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email address
          </label>
          <input
            id="email"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (e.currentTarget.value?.length > 0) {
                setShowPasswordLogin(true);
              } else {
                setShowPasswordLogin(false);
              }
              if (!isValidEmail) setIsValidEmail(true);
              setError('');
            }}
            className={`mt-1 w-full px-4 py-3 border ${
              isValidEmail ? 'border-gray-300 focus:border-purple-500' : 'border-red-500'
            } rounded-md focus:ring-2 focus:ring-purple-500 outline-none transition-all duration-200`}
          />
        </div>
        {showPasswordLogin && (
          <>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className="mt-1 w-full px-4 py-3 border border-gray-300 focus:border-purple-500 rounded-md focus:ring-2 focus:ring-purple-500 outline-none transition-all duration-200"
              />
            </div>
            <div className="flex justify-between items-center mt-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onForgotOpen();
                }}
                className="text-sm text-purple-600 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-all duration-200 transform hover:scale-[1.02] focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                'Sign In'
              )}
            </button>
          </>
        )}
      </form>
      {!showPasswordLogin && (
        <div className="flex flex-col items-center justify-center min-h-[300px] w-full max-w-md mx-auto">
          <div className="w-full space-y-4">
            {SOCIAL_PROVIDERS.map((provider) => (
              <button
                key={provider.id}
                onClick={() => handleSocialLogin(provider.id)}
                disabled={isLoading}
                className={`w-full flex items-center justify-center space-x-3 py-3 px-4 border border-gray-300 rounded-md ${provider.className} transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <img src={provider.icon} alt={provider.name} className="w-5 h-5" />
                <span>Continue with {provider.name}</span>
              </button>
            ))}
            <p className="text-sm text-gray-600 mt-2">
              Don't have an account?{' '}
              <button onClick={() => setIsSignupOpen(true)} className="text-purple-600 hover:underline">
                Sign up
              </button>
            </p>
          </div>
        </div>
      )}

      {isSignupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[10000]">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md">
            <button onClick={() => setIsSignupOpen(false)} className="absolute top-2 right-2 text-gray-600 hover:text-gray-900">
              ✖
            </button>
            <SignupPage onClose={() => setIsSignupOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SigninPage;
